import { gql, useQuery } from '@apollo/client';
import IconArrowLeft from '@apollo/icons/default/IconArrowLeft.svg';
import { Button, Card, CardBody, Spinner } from '@apollo/orbit';
import { GraphQLSchema } from 'graphql';
import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';

import {
  changelogRouteConfig,
  changelogSingleChangeRouteConfig,
  variantRouteConfig,
} from 'src/app/graph/routes';
import { NotFound } from 'src/components/common/notFound/NotFound';
import { useRouteParams } from 'src/hooks/useRouteParams';
import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import { GraphQLTypes } from 'src/lib/graphqlTypes';

import { GraphRef } from '../../hooks/useGraphRef';
import { ChangeLogEntry } from '../changeLogEntry/ChangeLogEntry';
import { SchemaTagDiffFragment } from '../schemaTagDiffFragment/SchemaTagDiffFragment';

const SingleChangePageLayout = ({
  historyItem,
  graphqlSchema,
}: {
  graphqlSchema: GraphQLSchema;
  historyItem: GraphQLTypes.SchemaTagDiffFragment | undefined | null;
}) => {
  const location = useLocation();

  return (
    <div className="overflow-y-auto p-8">
      <Card>
        <CardBody>
          {historyItem ? (
            <ChangeLogEntry
              graphqlSchema={graphqlSchema}
              historyItem={historyItem}
            />
          ) : (
            <NotFound title="Changelog entry not found" />
          )}
          <Button
            as={NavLink}
            to={changelogRouteConfig.locationFrom({
              location,
              fromRouteConfig: variantRouteConfig,
            })}
            leftIcon={<IconArrowLeft />}
            className={historyItem ? 'mt-8 self-start lg:ml-56' : 'self-center'}
            variant="hidden"
          >
            See all changes
          </Button>
        </CardBody>
      </Card>
    </div>
  );
};

export const SingleChangePage = ({
  graphRef,
  graphqlSchema,
}: {
  graphRef: GraphRef;
  graphqlSchema: GraphQLSchema;
}) => {
  const { version } = useRouteParams(changelogSingleChangeRouteConfig);

  const { data, loading, error } = useQuery<
    GraphQLTypes.SingleChangePage,
    GraphQLTypes.SingleChangePageVariables
  >(
    gql`
      query SingleChangePage($schemaTagId: ID!, $graphId: ID!) {
        service(id: $graphId) {
          id
          schemaTagById(id: $schemaTagId) {
            id
            ...SchemaTagDiffFragment
          }
        }
      }
      ${SchemaTagDiffFragment}
    `,
    {
      variables: {
        graphId: graphRef.graphId,
        schemaTagId: version,
      },
      context: appLinkContext({ catchErrors: [ignorePermissionsErrors] }),
    },
  );

  if (error) {
    throw error;
  }

  if (loading) {
    return (
      <div className="h-full" style={{ display: 'grid', placeItems: 'center' }}>
        <Spinner />
      </div>
    );
  }

  return (
    <SingleChangePageLayout
      graphqlSchema={graphqlSchema}
      historyItem={data?.service?.schemaTagById}
    />
  );
};
